import { useRef, useState } from "react";
import { RefreshCw, Video, Wand2, Maximize, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getResizedImageUrl,
  GRID_THUMBNAIL_WIDTH,
  GRID_THUMBNAIL_QUALITY,
} from "@/lib/imageUrl";
import { captureEvent } from "@/lib/analyticsQueue";
import { Overlay } from "@/components/flowly/infra/Overlay/Overlay";
import { LightboxCloseButton } from "../shared/LightboxCloseButton";
import { LightboxSidebar } from "../shared/LightboxSidebar";
import { LightboxActionButton } from "../shared/LightboxActionButton";
import { LightboxShareLikeRow } from "../shared/LightboxShareLikeRow";
import { LightboxDetailsSection } from "../shared/LightboxDetailsSection";
import { LightboxCollapsibleSection } from "../shared/LightboxCollapsibleSection";
import { LightboxDeleteButton } from "../shared/LightboxDeleteButton";
import { LightboxNavigation } from "../shared/LightboxNavigation";
import { PIPELINE_TYPES } from "../shared/constants";
import { useSwipeNavigation } from "../shared/useSwipeNavigation";
import { ANALYTICS_EVENTS, PIPELINE_TO_FLOW } from "@shared/schemas/analytics";
import { useImageLightbox } from "./useImageLightbox";
import type { ImageLightboxProps } from "./types";
import { requestFullscreenOn } from "@/lib/fullscreen";

export function ImageLightbox({
  imageUrl,
  thumbnailUrl,
  imageId,
  referenceImageUrl,
  createdAt,
  resolution,
  aspectRatio,
  pipeline,
  prompt,
  model,
  onClose,
  onRegenerate,
  onDownload,
  onShare,
  onUseSourceImage,
  onDelete,
  navigation,
}: ImageLightboxProps) {
  const mediaContainerRef = useRef<HTMLDivElement>(null);

  // Blur-up: show an instantly-available low-res placeholder while the full-res
  // image downloads, then crossfade to it — so navigating on a slow connection
  // moves immediately instead of freezing on the previous image. Track load
  // state by URL (not a reset effect) so it's race-free across navigation.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);
  // Placeholder URLs that failed to load (e.g. resize not enabled on the zone →
  // 403). Falling back to the full image avoids a broken-image placeholder.
  const [placeholderErroredUrl, setPlaceholderErroredUrl] = useState<string | null>(null);
  const fullLoaded = loadedUrl === imageUrl;
  const fullErrored = erroredUrl === imageUrl;
  // Derive the placeholder from the same resize the grid already cached. In dev
  // resize is a no-op (=== imageUrl) → no separate placeholder, spinner only.
  const placeholder =
    thumbnailUrl ??
    getResizedImageUrl(imageUrl, GRID_THUMBNAIL_WIDTH, {
      quality: GRID_THUMBNAIL_QUALITY,
    });
  const hasPlaceholder =
    !!placeholder && placeholder !== imageUrl && placeholderErroredUrl !== placeholder;

  const handleFullscreen = () => {
    // Cross-browser + no-op on iOS Safari (kills FRONTEND-T).
    requestFullscreenOn(mediaContainerRef.current);
  };

  const swipeHandlers = useSwipeNavigation({
    onNext: navigation?.onNext,
    onPrev: navigation?.onPrev,
    enabled: !!navigation,
  });

  const {
    t,
    liked,
    formattedDate,
    modelDisplayName,
    handleToggleLike,
    handleEdit,
    handleAnimate,
    handleUseSource,
  } = useImageLightbox({
    imageUrl,
    referenceImageUrl,
    createdAt,
    model,
    onClose,
    onUseSourceImage,
    navigation,
  });

  const handleTrackedDownload = () => {
    if (!onDownload) return;
    captureEvent(ANALYTICS_EVENTS.CONTENT_DOWNLOAD, { type: "image", flow: PIPELINE_TO_FLOW[pipeline ?? ""] ?? pipeline });
    onDownload();
  };

  const handleTrackedShare = () => {
    if (!onShare) return;
    captureEvent(ANALYTICS_EVENTS.CONTENT_SHARE, { type: "image", flow: PIPELINE_TO_FLOW[pipeline ?? ""] ?? pipeline });
    onShare();
  };

  const getPipelineDisplay = (type: string | undefined) => {
    if (!type) return undefined;
    if (type === PIPELINE_TYPES.AD) return t.lightbox.pipelineAd;
    if (type === PIPELINE_TYPES.EDIT) return t.lightbox.pipelineEdit;
    if (type === PIPELINE_TYPES.DIRECTOR) return t.lightbox.pipelineDirector;
    return type;
  };

  return (
    <Overlay isOpen onClose={onClose} className="bg-black/90 p-0 cursor-pointer animate-fade-in" zIndex="z-[100]">
      <div
        className="flex flex-col md:flex-row gap-4 md:gap-6 w-full mx-4 md:mx-6 animate-scale-in relative cursor-default max-h-[90vh] overflow-y-auto md:overflow-visible md:h-[calc(100vh-80px)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Image - Full Resolution */}
        <div className="flex-shrink-0 md:flex-1 flex items-center justify-center order-1 md:order-2 relative" {...swipeHandlers}>
          {/* Navigation Arrows */}
          {navigation && (
            <LightboxNavigation
              currentIndex={navigation.currentIndex}
              totalItems={navigation.totalItems}
              canGoNext={navigation.canGoNext}
              canGoPrev={navigation.canGoPrev}
              onNext={navigation.onNext}
              onPrev={navigation.onPrev}
            />
          )}

          <div ref={mediaContainerRef} className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-white/10 max-h-[72vh] md:max-h-[calc(100vh-80px)] bg-black">
            {hasPlaceholder ? (
              <>
                {/* Low-res placeholder — defines the box size, shown instantly
                    (already cached from the grid) until the full-res arrives. */}
                <img
                  src={placeholder}
                  alt=""
                  aria-hidden
                  draggable={false}
                  onError={() => setPlaceholderErroredUrl(placeholder)}
                  className="w-auto h-auto max-w-full max-h-[72vh] md:max-h-[calc(100vh-100px)] object-contain select-none"
                />
                {/* Full-res — overlays exactly, crossfades in on load. */}
                <img
                  src={imageUrl}
                  alt="Full size"
                  onLoad={() => setLoadedUrl(imageUrl)}
                  onError={() => setErroredUrl(imageUrl)}
                  className={cn(
                    "absolute inset-0 w-full h-full object-contain transition-opacity duration-300",
                    fullLoaded ? "opacity-100" : "opacity-0",
                  )}
                />
              </>
            ) : (
              <img
                src={imageUrl}
                alt="Full size"
                onLoad={() => setLoadedUrl(imageUrl)}
                onError={() => setErroredUrl(imageUrl)}
                className="w-auto h-auto max-w-full max-h-[72vh] md:max-h-[calc(100vh-100px)] object-contain"
              />
            )}
            {/* Spinner while the full-res downloads (hidden once loaded/failed). */}
            {!fullLoaded && !fullErrored && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Loader2 className="w-8 h-8 text-white/70 animate-spin drop-shadow-lg" />
              </div>
            )}
          </div>
        </div>

        {/* Options Sidebar */}
        <LightboxSidebar title={t.lightbox.options}>
          <LightboxCloseButton
            onClose={onClose}
            backLabel={t.lightbox.backToStudio}
          />

          {/* Primary actions — always visible */}
          <LightboxShareLikeRow
            onDownload={handleTrackedDownload}
            downloadLabel={t.lightbox.download}
            onShare={handleTrackedShare}
            shareLabel={t.lightbox.share}
            liked={liked}
            onToggleLike={handleToggleLike}
          />

          {/* Advanced — open on desktop, collapsed on mobile */}
          <LightboxCollapsibleSection title={t.lightbox.advancedSettings} defaultOpenDesktop>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-3">
              <LightboxActionButton
                icon={<Wand2 className="w-4 h-4 text-amber-400" />}
                label={t.lightbox.editImage}
                onClick={handleEdit}
                colorClass="bg-amber-500/10 hover:bg-amber-500/20"
                iconColorClass="bg-amber-500/20"
              />
              {onRegenerate && (
                <LightboxActionButton
                  icon={<RefreshCw className="w-4 h-4 text-primary" />}
                  label={t.lightbox.regenerate}
                  onClick={onRegenerate}
                  colorClass="bg-primary/10 hover:bg-primary/20"
                  iconColorClass="bg-primary/20"
                />
              )}
              <LightboxActionButton
                icon={<Video className="w-4 h-4 text-brand" />}
                label={t.lightbox.animate}
                onClick={handleAnimate}
                colorClass="bg-brand/10 hover:bg-brand/20"
                iconColorClass="bg-brand/20"
              />
              <LightboxActionButton
                icon={<Maximize className="w-4 h-4 text-sky-400" />}
                label={t.lightbox.fullscreen}
                onClick={handleFullscreen}
                colorClass="bg-sky-500/10 hover:bg-sky-500/20"
                iconColorClass="bg-sky-500/20"
              />
            </div>
          </LightboxCollapsibleSection>

          {/* Delete Button */}
          {onDelete && <LightboxDeleteButton onDelete={onDelete} />}

          {/* Details — collapsed by default */}
          <LightboxCollapsibleSection title={t.lightbox.imageDetails}>
            <LightboxDetailsSection
              formattedDate={formattedDate}
              dateLabel={t.lightbox.creationDate}
              model={modelDisplayName}
              modelLabel={modelDisplayName ? t.lightbox.model : undefined}
              sourceImageUrl={referenceImageUrl}
              sourceImageLabel={t.lightbox.sourceImage}
              resolution={resolution}
              resolutionLabel={t.lightbox.resolution}
              aspectRatio={aspectRatio}
              aspectRatioLabel={t.lightbox.aspectRatio}
              pipeline={getPipelineDisplay(pipeline)}
              pipelineLabel={pipeline ? t.lightbox.pipeline : undefined}
              prompt={prompt}
              promptLabel={prompt ? t.lightbox.prompt : undefined}
              onSourceImageClick={referenceImageUrl ? handleUseSource : undefined}
            />
          </LightboxCollapsibleSection>
        </LightboxSidebar>
      </div>
    </Overlay>
  );
}
